/** The recipient standing for every owner of the company. Mirrors the backend. */
export const OWNERS_RECIPIENT = "role:OWNER";

/** How often a digest goes out. Mirrors the backend registry. */
export type NotificationFrequency = "daily" | "weekly" | "monthly";

/**
 * The company's setting for one subject. There is no per-member preference: a digest is
 * part of how a team divides its work, so it is configured once in Company > Settings,
 * recipients included.
 */
export type NotificationSetting = {
  topic: string;
  enabled: boolean;
  frequency: NotificationFrequency;
  /** Local hour of the day, in the company's timezone. */
  hour: number;
  /** ISO weekday (weekly) or day of the month (monthly). */
  day: number;
  /**
   * `users.id` of the recipients, and/or {@link OWNERS_RECIPIENT} for every owner. The
   * server never returns it empty: nobody picked means the owners.
   */
  recipients: string[];
  channels: string[];
  /** `false` while the subject still runs on its own defaults. */
  configured: boolean;
};

/** One subject as the settings screen lists it. */
export type NotificationTopicView = {
  key: string;
  /** The module it belongs to, `""` for the ones every company has. */
  module: string;
  /** `backlog` stays silent when there is nothing; `report` always goes out. */
  kind: "backlog" | "report";
  title: string;
  description: string;
  frequencies: NotificationFrequency[];
  setting: NotificationSetting;
  last_sent_at: number;
  next_due_at: number;
};

/**
 * Somebody the company can send a digest to: **an access** (a login with an e-mail
 * address and a role), not a team member. Most of a salon's team has no login and so no
 * address to write to, and a login that is not on the team — a manager, an accountant —
 * can perfectly well receive one.
 */
export type NotificationCandidate = {
  user_id: string;
  name: string;
  email: string;
  /** An owner, and so covered by the "Owners" recipient. */
  is_owner: boolean;
  /** The subjects this person may receive, given their role. */
  eligible_topics: string[];
};

/** What a caller may change. `null` puts a field back to the subject's default. */
export type NotificationSettingPatch = {
  enabled?: boolean | null;
  frequency?: NotificationFrequency | null;
  hour?: number | null;
  day?: number | null;
  recipients?: string[] | null;
};

/** What a digest for a subject would say right now (preview, sends nothing). */
export type NotificationDigest = {
  count: number;
  summary: string;
  details?: [string, string][];
  path?: string;
};
